import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MobileOverlay, TwoColumnLayout } from '@/components/layout';
import SwipeableTabContent from '@/components/SwipeableTabContent';
import { useBreakpointDown } from '@/hooks/useBreakpoint';

export interface TabConfig {
  component: React.FC;
  path: string;
}

interface ContentLayoutProps {
  sidebar: React.ReactNode;
  tabs: TabConfig[];
  mobileOverlay?: React.ReactNode;
  mobileFilter?: React.ReactNode;
}

/**
 * ContentLayout — shared parent sublayout for all tabbed pages.
 *
 * Handles:
 * - TwoColumnLayout rendering (sidebar + main)
 * - Mobile: SwipeableTabContent wrapping all tab components, swipe-to-URL sync
 * - Desktop: <Outlet /> for route-based tab rendering
 * - Mobile overlay pattern (form replaces tabs)
 *
 * Specific layouts (AccountLayout, AdminLayout, etc.) configure ContentLayout
 * with their sidebar and tabs, and wrap it with their own context provider.
 */
export const ContentLayout: React.FC<ContentLayoutProps> = ({
  sidebar,
  tabs,
  mobileOverlay,
  mobileFilter,
}) => {
  const isMobile = useBreakpointDown('lg');
  const location = useLocation();
  const navigate = useNavigate();

  // Longest-prefix match so deep sub-routes (e.g.
  // `/tournaments/:id/leaderboard/:userId`) still highlight their parent tab
  // (`/tournaments/:id/leaderboard`). Exact match wins ties via natural sort
  // order; ascending-length scan keeps the longest matching `path` selected.
  let bestIdx = -1;
  let bestLen = -1;
  tabs.forEach((tab, i) => {
    const matchesExact = location.pathname === tab.path;
    const matchesPrefix = location.pathname.startsWith(tab.path + '/');
    if ((matchesExact || matchesPrefix) && tab.path.length > bestLen) {
      bestIdx = i;
      bestLen = tab.path.length;
    }
  });
  const activeTabIndex = Math.max(0, bestIdx);

  const handleSlideChange = (index: number) => {
    // Swiper reports the initial / synced slide too; re-navigating to the
    // current tab would drop its query string (e.g. list filters).
    if (index === activeTabIndex) return;
    if (tabs[index]) {
      navigate(tabs[index].path, { replace: true });
    }
  };

  if (isMobile && mobileOverlay) {
    return (
      <TwoColumnLayout sidebar={sidebar} mobileFilter={mobileFilter}>
        <MobileOverlay>{mobileOverlay}</MobileOverlay>
      </TwoColumnLayout>
    );
  }

  return (
    <TwoColumnLayout sidebar={sidebar} mobileFilter={mobileFilter}>
      {isMobile ? (
        <SwipeableTabContent activeIndex={activeTabIndex} onSlideChange={handleSlideChange}>
          {tabs.map(({ component: Tab }, i) => <Tab key={i} />)}
        </SwipeableTabContent>
      ) : (
        <Outlet />
      )}
    </TwoColumnLayout>
  );
};

export default ContentLayout;
